import FlagFootballPlay from "../sports/flag-football/play/flag-football-play";
import { FootballService } from "./football-service";
import TransitionBuilder from "../sports/football/state/transition-builder";
import LocalDataStrategy from "./api-client/base-data-strategy";
import FlagFootballTransitionConfig from "../sports/flag-football/state/flag-football-play-transtions";
import ContextService from "./context-service";
import FootballTransition from "../sports/football/state/football-transition";
import FootballState from "../sports/football/state/football-state";
import FlagPatCompleteStats from "../sports/flag-football/rules/general/scoring/pat-complete-stats";
import { isSafety } from "../sports/football/utils/context-utils";

class FlagFootballService extends FootballService {
    constructor() {
        super();
        this.localDataStrategy = new LocalDataStrategy();
    }

    async addPlay(gameId) {
        const play = new FlagFootballPlay();
        return this.baseAddPlay(gameId, play);
    }

    async getMetadata() {
        const transitionMetadata = FlagFootballTransitionConfig.map(
            (config) => {
                return Object.assign(
                    config,
                    TransitionBuilder.getByName(config.name)
                );
            }
        );

        return {
            transitions: transitionMetadata
        };
    }

    async _createPendingPlays(gameId) {
        let gameContext = await ContextService.getGameContext(gameId);
        const { triggerExtraOvertime, triggerPAT } = gameContext;
        const { triggerDrive } = gameContext.getDriveContext();
        if (triggerPAT) {
            await this._finalizeDrive(gameContext);
            await this._createPendingPATPlay(gameId, gameContext.getTime());
        } else if (triggerDrive) {
            await this._finalizeDrive(gameContext);
            if (gameContext.getOvertimePeriod() && triggerExtraOvertime) {
                await this._createStartPeriodPlay(
                    gameId,
                    gameContext.getCurrentPeriod() + 1
                );
            }
            await this._startDriveWithoutPlay(gameId);
        }
    }

    _getApplicableTransitions(
        gameContext,
        play,
        transitions,
        fragment,
        coinTossRequired
    ) {
        if (fragment.associatedState === FootballState.AddPlay) {
            transitions = this._hideAddPlayTransitions(
                gameContext,
                transitions
            );
        } else {
            transitions = this._hideApplicableTransitions(
                gameContext,
                play,
                transitions,
                fragment,
                coinTossRequired
            );
        }
        return transitions;
    }

    _hideApplicableTransitions(
        gameContext,
        play,
        transitions,
        fragment,
        coinTossRequired
    ) {
        // TODO: Maybe we can remove this.
        transitions = fragment
            ? play.getStateTransitions(fragment.associatedState)
            : play.getAllowedTransitions();
        //

        if (coinTossRequired) {
            return transitions.filter(
                (transition) => transition.name === FootballTransition.CoinToss
            );
        } else {
            // Drives are created automatically; never offer a manual Start of
            // Drive action to operators.
            transitions = transitions.filter(
                (transition) =>
                    transition.name !== FootballTransition.StartDrive
            );
            if (fragment && !isSafety(play, fragment, gameContext)) {
                transitions = transitions.filter(
                    (t) => FootballTransition.Safety !== t.name
                );
            }

            return transitions;
        }
    }

    _hideAddPlayTransitions(gameContext, transitions) {
        const { triggerPAT, currentDriveId, coinTossRequired } = gameContext;

        const allowedPatTransitions = [
            FootballTransition.PatTry,
            FootballTransition.PlayPenalty,
            FootballTransition.GameControl
        ];
        const generalTransitions = [
            FootballTransition.PlayPenalty,
            FootballTransition.GameControl,
            FootballTransition.Pass,
            FootballTransition.Rush,
            FootballTransition.Punt,
            FootballTransition.Spike,
            FootballTransition.KneelDown
        ];
        if (triggerPAT) {
            transitions = transitions.filter((t) =>
                allowedPatTransitions.some(
                    (transition) => transition === t.name
                )
            );
        } else if (!currentDriveId || coinTossRequired) {
            transitions = this._filterGameCtrlTransitions(transitions);
        } else {
            if (gameContext.getOvertimePeriod() >= 1) {
                generalTransitions.push(FootballTransition.PatTry);
            }
            transitions = transitions.filter((t) =>
                generalTransitions.some((transition) => transition === t.name)
            );
        }
        return transitions;
    }

    async updatePatStats(originalPlay, originalFragment, updatedFragment) {
        const gameContext = await ContextService.getGameContext(
            originalPlay.gameId
        );
        const { statsFragment, points } =
            FlagPatCompleteStats._resolvePatStatsFragmentAndPoints(
                originalPlay,
                updatedFragment,
                gameContext
            );

        return this._doUpdatePatStats({ statsFragment, points });
    }
}

export default new FlagFootballService();
